import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { DollarSign, TrendingDown, ArrowRight, Calculator, Phone, CheckCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { trackEvent } from '../utils/analytics';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const PRICING = {
  semaglutide: { brand: 1100, compounded: 179 },
  tirzepatide: { brand: 1200, compounded: 299 }
};

const CostCalculator = () => {
  const navigate = useNavigate();
  const [medication, setMedication] = useState('semaglutide');
  const [months, setMonths] = useState(6);

  const calculations = useMemo(() => {
    const prices = PRICING[medication];
    const brandTotal = prices.brand * months;
    const compoundedTotal = prices.compounded * months;
    const savings = brandTotal - compoundedTotal;
    const savingsPercent = Math.round((savings / brandTotal) * 100);
    
    return {
      brandMonthly: prices.brand,
      compoundedMonthly: prices.compounded,
      brandTotal,
      compoundedTotal,
      savings,
      savingsPercent
    };
  }, [medication, months]);

  const chartData = [
    { name: 'Brand Name', cost: calculations.brandTotal, fill: '#EF4444' },
    { name: 'Compounded', cost: calculations.compoundedTotal, fill: '#22C55E' }
  ];

  const handleCTAClick = () => {
    trackEvent('cost_calculator_cta_clicked', { medication, months });
    navigate('/calculator');
  };

  return (
    <div className="min-h-screen bg-[#0B1020] text-white pb-32">
      <SEOHead 
        title="GLP-1 Cost Comparison Calculator | Weight Loss Care Team"
        description="Compare brand name vs compounded GLP-1 medication costs. See how much you can save on semaglutide and tirzepatide programs."
        path="/cost-calculator"
      />

      {/* Header */}
      <div className="bg-gradient-to-b from-[#1E293B] to-[#0B1020] pt-8 pb-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/" className="text-[#94A3B8] hover:text-white text-sm mb-4 inline-block">
            ← Back to Home
          </Link>
          <div className="flex items-center justify-center gap-2 mb-4">
            <Calculator className="w-8 h-8 text-[#22C55E]" />
            <h1 className="text-2xl sm:text-3xl font-bold">GLP-1 Cost Calculator</h1>
          </div>
          <p className="text-[#94A3B8] text-base sm:text-lg">
            See the real cost difference between brand name and compounded medications
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-4">
        {/* Input Card */}
        <Card className="bg-[#1E293B] border-[#334155] p-6 mb-6">
          {/* Medication Toggle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Medication</label>
            <div className="grid grid-cols-2 gap-2">
              {['semaglutide', 'tirzepatide'].map((med) => (
                <button
                  key={med}
                  onClick={() => setMedication(med)}
                  className={`py-3 px-4 rounded-lg font-medium transition-all ${
                    medication === med
                      ? 'bg-[#3B82F6] text-white'
                      : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`medication-${med}`}
                >
                  {med.charAt(0).toUpperCase() + med.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Months Toggle */}
          <div>
            <label className="block text-sm font-medium text-[#94A3B8] mb-3">Program Length</label>
            <div className="grid grid-cols-3 gap-2">
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  onClick={() => setMonths(m)}
                  className={`py-3 px-4 rounded-lg font-medium transition-all ${
                    months === m
                      ? 'bg-[#3B82F6] text-white'
                      : 'bg-[#0B1020] text-[#94A3B8] hover:bg-[#334155]'
                  }`}
                  data-testid={`months-${m}`}
                >
                  {m} Months
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Results Card */}
        <Card className="bg-[#1E293B] border-[#334155] p-6 mb-6">
          <h2 className="text-lg font-semibold mb-6 text-center">Cost Comparison</h2>
          
          {/* Side by Side Comparison */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Brand Name */}
            <div className="bg-[#0B1020] rounded-lg p-4 border border-red-500/30">
              <p className="text-sm text-[#94A3B8] mb-1">Brand Name</p>
              <p className="text-2xl font-bold text-red-400">${calculations.brandMonthly}</p>
              <p className="text-xs text-[#94A3B8]">per month</p>
              <div className="mt-3 pt-3 border-t border-[#334155]">
                <p className="text-sm text-[#94A3B8]">{months}-month total</p>
                <p className="text-xl font-bold text-red-400">${calculations.brandTotal.toLocaleString()}</p>
              </div>
            </div>

            {/* Compounded */}
            <div className="bg-[#0B1020] rounded-lg p-4 border border-green-500/30">
              <p className="text-sm text-[#94A3B8] mb-1">Compounded</p>
              <p className="text-2xl font-bold text-green-400">${calculations.compoundedMonthly}</p>
              <p className="text-xs text-[#94A3B8]">per month</p>
              <div className="mt-3 pt-3 border-t border-[#334155]">
                <p className="text-sm text-[#94A3B8]">{months}-month total</p>
                <p className="text-xl font-bold text-green-400">${calculations.compoundedTotal.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Savings Display */}
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg p-6 text-center border border-green-500/30 mb-4">
            <p className="text-sm text-[#94A3B8] mb-1">Your Total Savings</p>
            <p className="text-4xl sm:text-5xl font-bold text-green-400" data-testid="total-savings">
              ${calculations.savings.toLocaleString()}
            </p>
            <p className="text-green-400 mt-1">
              Save {calculations.savingsPercent}% over {months} months
            </p>
          </div>

          {/* All Dosages Included Callout */}
          <div className="flex items-center justify-center gap-2 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-lg p-3 mb-6">
            <CheckCircle className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
            <p className="text-[#22C55E] text-sm font-medium">
              All dosages included — your price never increases as you progress.
            </p>
          </div>

          {/* Bar Chart */}
          <div className="h-48 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" stroke="#94A3B8" tickFormatter={(v) => `$${v.toLocaleString()}`} />
                <YAxis type="category" dataKey="name" stroke="#94A3B8" width={100} />
                <Tooltip 
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Total Cost']}
                  contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                />
                <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Context Quote */}
        <Card className="bg-[#1E293B]/50 border-[#334155] p-6 mb-6">
          <p className="text-[#94A3B8] text-center italic">
            "Most people spend more on coffee and subscriptions annually than a full medically supervised GLP-1 program costs through a compounding pharmacy."
          </p>
        </Card>

        {/* CTA */}
        <Button
          onClick={handleCTAClick}
          className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-6 text-lg font-semibold rounded-xl"
          data-testid="see-forecast-cta"
        >
          See Your Personal Forecast
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>

        {/* Contact */}
        <div className="mt-6 text-center">
          <a 
            href="sms:8016518363" 
            className="text-[#94A3B8] hover:text-white inline-flex items-center gap-2"
          >
            <Phone className="w-4 h-4" />
            Questions? Text Bryan
          </a>
        </div>
      </div>
    </div>
  );
};

export default CostCalculator;
